import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FiMenu, FiX, FiHome, FiList, FiUser, FiLogOut, FiUsers, FiType, FiChevronDown, FiMaximize2, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import logoImage from '../assets/logo.png';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 1000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
  
  > div:first-child {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    padding-left: 0;
    margin-left: 0;
  }
  
  > div:last-child {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 800;
  color: black;
  text-decoration: none;
  letter-spacing: -0.5px;
  transition: all 0.3s ease;
  margin: 0;
  padding: 0;
  
  &:hover {
    transform: scale(1.05);
    filter: brightness(1.1);
  }
`;

const LogoImage = styled.img`
  height: 50px;
  width: auto;
  max-width: 120px;
  object-fit: contain;
  margin: 0;
  padding: 0;
`;

const Nav = styled.nav.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  display: flex;
  align-items: center;
  gap: 32px;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    flex-direction: column;
    padding: 20px;
    border-top: 1px solid #e5e7eb;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const NavLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== 'active',
})`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 12px;
  color: ${props => props.active ? 'white' : '#6b7280'};
  text-decoration: none;
  font-weight: 600;
  font-size: 10px;
  transition: all 0.3s ease;
  background: ${props => props.active ? 'black' : 'transparent'};
  color: ${props => props.active ? 'white' : '#6b7280'};
  box-shadow: ${props => props.active ? '0 4px 15px rgba(0, 0, 0, 0.4)' : 'none'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    color: black;
    background: rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
`;

const UserProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: none;
  border-radius: 12px;
  color: #374151;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  min-width: 220px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  margin-top: 12px;
  opacity: ${props => props.isOpen ? '1' : '0'};
  transform: ${props => props.isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)'};
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 18px;
  background: none;
  border: none;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent);
    transition: left 0.3s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    color: #1e40af;
    transform: translateX(4px);
  }

  &:first-child {
    border-radius: 16px 16px 0 0;
  }

  &:last-child {
    border-radius: 0 0 16px 16px;
  }

  svg {
    transition: all 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.1);
    color: #3b82f6;
  }
`;

const LogoutButton = styled(DropdownItem)`
  color: #dc2626;
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;

  &:hover {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    color: #b91c1c;
    transform: translateX(4px);
  }

  &:hover svg {
    color: #dc2626;
    transform: scale(1.1);
  }
`;

const HomeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
    pointer-events: none;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [builderStep, setBuilderStep] = useState(1);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const userMenuRef = useRef(null);

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
    setIsUserDropdownOpen(false);
  };

  const handleUserDropdownToggle = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  const handleUsersClick = () => {
    navigate('/users');
    setIsUserDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };

    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserDropdownOpen]);

  // Listen for builder step updates to reflect active state in header
  useEffect(() => {
    const onStepUpdate = (e) => {
      if (e && e.detail && typeof e.detail.stepId === 'number') {
        setBuilderStep(e.detail.stepId);
      }
    };
    window.addEventListener('builder:step:update', onStepUpdate);
    return () => window.removeEventListener('builder:step:update', onStepUpdate);
  }, []);

  const emitStepRequest = (stepId) => {
    const event = new CustomEvent('builder:step:request', { detail: { stepId } });
    window.dispatchEvent(event);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <div>
          <Logo to="/">
            Gastro Forms
          </Logo>
          {location.pathname.startsWith('/builder') && (
            <HomeButton onClick={() => {
              console.log('Home button clicked, navigating to home');
              navigate('/');
            }}>
              <FiHome />
              Home
            </HomeButton>
          )}
        </div>
        
        <div>
          <Nav isOpen={isMenuOpen}>
            {location.pathname.startsWith('/builder') ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                {[
                  { id: 1, title: 'Form Details' },
                  { id: 2, title: 'Add Questions' },
                  { id: 3, title: 'Preview & Test' },
                  { id: 4, title: 'Save & Share' },
                ].map(step => (
                  <button
                    key={step.id}
                    onClick={() => emitStepRequest(step.id)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '9999px',
                      border: '1px solid #e5e7eb',
                      background: builderStep === step.id ? 'black' : 'white',
                      color: builderStep === step.id ? 'white' : '#374151',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                    title={step.title}
                  >
                    {step.title}
                  </button>
                ))}
              </div>
            ) : (
              <>
                {isAdmin() && (
                  <>
                    <NavLink to="/qr-generator" active={isActive('/qr-generator')}>
                      <FiMaximize2 />
                      Generate QR
                    </NavLink>
                    <NavLink to="/qr-manager" active={isActive('/qr-manager')}>
                      <FiRefreshCw />
                      My QR Codes
                    </NavLink>
                  </>
                )}
              </>
            )}
          </Nav>
          
          <UserMenu ref={userMenuRef}>
            <UserProfileButton onClick={handleUserDropdownToggle}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
              }}>
                {user?.username?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                  {user?.username}
                </span>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                  {user?.role}
                </span>
              </div>
              <FiChevronDown style={{ 
                fontSize: '14px', 
                transform: isUserDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', 
                transition: 'transform 0.3s ease',
                color: '#6b7280'
              }} />
            </UserProfileButton>
            
            <UserDropdown isOpen={isUserDropdownOpen}>
              {/* User Profile Header */}
              <div style={{
                padding: '16px 18px',
                borderBottom: '1px solid #f1f5f9',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: '16px 16px 0 0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}>
                    {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', marginBottom: '2px' }}>
                      {user?.username}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>
                      {user?.role}
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              {(user?.role === 'admin' || user?.role === 'Super Admin' || user?.permissions?.includes('user.read')) && (
                <DropdownItem onClick={handleUsersClick}>
                  <FiUsers />
                  <span>Manage Users</span>
                </DropdownItem>
              )}
              <LogoutButton onClick={handleLogout}>
                <FiLogOut />
                <span>Sign Out</span>
              </LogoutButton>
            </UserDropdown>
          </UserMenu>
          
          <MobileMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <FiX /> : <FiMenu />}
          </MobileMenuButton>
        </div>
      </HeaderContent>
    </HeaderContainer>
  );
}

export default Header;
